import React, { useState, useEffect } from 'react';
import type { Debt, DebtCategory } from '../types/debt';
import { createDebt, updateDebt } from '../services/debtService';
import { X, DollarSign, Building, Calendar, Tag, AlignLeft } from 'lucide-react';

interface DebtModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingDebt?: Debt | null;
}

const CATEGORIES: { value: DebtCategory; label: string }[] = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'buy_now_pay_later', label: 'Buy Now Pay Later (e.g. Tabby)' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'other', label: 'Other' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'CAD', 'AUD'];

const DebtModal: React.FC<DebtModalProps> = ({ isOpen, onClose, onSaved, editingDebt }) => {
  const [creditorName, setCreditorName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [category, setCategory] = useState<DebtCategory>('credit_card');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingDebt) {
      setCreditorName(editingDebt.creditorName);
      setAmount(String(editingDebt.amount));
      setCurrency(editingDebt.currency);
      setCategory(editingDebt.category);
      setDescription(editingDebt.description);
      setDueDate(editingDebt.dueDate || '');
    } else {
      setCreditorName(''); setAmount(''); setCurrency('USD');
      setCategory('credit_card'); setDescription(''); setDueDate('');
    }
  }, [editingDebt, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!creditorName.trim() || !amount || isNaN(Number(amount)) || Number(amount) <= 0) return;
    setLoading(true);
    const data = {
      creditorName: creditorName.trim(),
      amount: Number(amount), currency, category,
      description: description.trim(),
      dueDate: dueDate || undefined,
    };
    if (editingDebt) {
      updateDebt(editingDebt.id, data);
    } else {
      createDebt(data);
    }
    setLoading(false);
    onSaved();
    onClose();
  };

  const inputClass = "w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-gray-500 bg-white/5 border border-white/10 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative w-full max-w-md rounded-2xl shadow-2xl fade-in-up" style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <h2 className="text-lg font-semibold text-white">{editingDebt ? 'Edit Debt' : 'Add New Debt'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Creditor */}
          <div>
            <label className={labelClass}>Creditor / Bank / Service</label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input id="debtCreditorName" className={`${inputClass} pl-9`} placeholder="e.g. Tabby, Amex, Chase" value={creditorName} onChange={(e) => setCreditorName(e.target.value)} required />
            </div>
          </div>

          {/* Amount + Currency */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelClass}>Total Due</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input id="debtAmount" className={`${inputClass} pl-9`} type="number" step="0.01" min="0.01" placeholder="0.00"
                  value={amount} onChange={(e) => setAmount(e.target.value)} required />
              </div>
            </div>
            <div>
              <label className={labelClass}>Currency</label>
              <select className={`${inputClass} appearance-none`} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c} className="bg-gray-800">{c}</option>)}
              </select>
            </div>
          </div>

          {/* Category + Due Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Category</label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <select className={`${inputClass} pl-9 appearance-none`} value={category} onChange={(e) => setCategory(e.target.value as DebtCategory)}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value} className="bg-gray-800">{c.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Due Date <span className="text-gray-600">(optional)</span></label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input className={`${inputClass} pl-9`} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>Notes <span className="text-gray-600">(optional)</span></label>
            <div className="relative">
              <AlignLeft className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
              <textarea className={`${inputClass} pl-9 resize-none`} rows={2} placeholder="Account #, what is this for?"
                value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 transition-colors shadow-lg shadow-indigo-500/25">
              {loading ? 'Saving…' : editingDebt ? 'Update Debt' : 'Add Debt'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DebtModal;
